import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Trash2, RotateCcw, FileText, User, Building2, Boxes, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

import { API_BASE as API } from '../config';

const TABS = [
  { id: 'claims', label: 'Claims', icon: FileText },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'notes', label: 'Notes', icon: MessageSquare },
  { id: 'users', label: 'Users', icon: User },
  { id: 'companies', label: 'Companies', icon: Building2 },
  { id: 'organizations', label: 'Organizations', icon: Boxes },
];

const RecycleBin = () => {
  const { user, activeOrgId, userOrgs } = useAuth();
  const isInOrgView = user?.account_type === 'provider' && !!activeOrgId;
  const activeOrg = isInOrgView ? userOrgs.find(o => o.org_id === activeOrgId) : null;
  const [activeTab, setActiveTab] = useState('claims');
  const [claims, setClaims] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const orgParam = isInOrgView ? `?org_id=${activeOrgId}` : '';
    try {
      const [claimsRes, docsRes, notesRes, usersRes, companiesRes, orgsRes] = await Promise.all([
        axios.get(`${API}/recycle-bin/claims${orgParam}`, { withCredentials: true }),
        axios.get(`${API}/recycle-bin/documents${orgParam}`, { withCredentials: true }),
        axios.get(`${API}/recycle-bin/notes${orgParam}`, { withCredentials: true }),
        axios.get(`${API}/recycle-bin/users${orgParam}`, { withCredentials: true }),
        axios.get(`${API}/recycle-bin/companies${orgParam}`, { withCredentials: true }),
        axios.get(`${API}/recycle-bin/organizations${orgParam}`, { withCredentials: true }),
      ]);
      setClaims(claimsRes.data);
      setDocuments(docsRes.data);
      setNotes(notesRes.data);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setOrganizations(orgsRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [activeOrgId]);

  const handleRestore = async (type, id) => {
    try {
      await axios.post(`${API}/recycle-bin/${type}/${encodeURIComponent(id)}/restore`, {}, { withCredentials: true });
      toast.success('Restored successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    }
  };

  const handlePurge = async (type, id) => {
    if (!window.confirm('Permanently delete? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/recycle-bin/${type}/${encodeURIComponent(id)}`, { withCredentials: true });
      toast.success('Permanently deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getCount = (tab) => {
    const map = { claims: claims.length, documents: documents.length, notes: notes.length, users: users.length, companies: companies.length, organizations: organizations.length };
    return map[tab] || 0;
  };

  const ActionButtons = ({ type, id }) => (
    <div className="flex gap-1 shrink-0">
      <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 w-8 p-0" onClick={() => handleRestore(type, id)} title="Restore" data-testid={`restore-${type}-${id}`}>
        <RotateCcw className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" onClick={() => handlePurge(type, id)} title="Delete permanently" data-testid={`purge-${type}-${id}`}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  const EmptyState = () => (
    <div className="text-center py-10 text-gray-400">
      <Trash2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p className="text-sm">Nothing in the recycle bin</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="recycle-bin-page">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">{isInOrgView ? `${activeOrg?.name || 'Organization'} — Recycle Bin` : 'Recycle Bin'}</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1" data-testid="recycle-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            {getCount(tab.id) > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20' : 'bg-gray-300 text-gray-700'}`}>
                {getCount(tab.id)}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin" /></div>
      ) : (
        <Card>
          <CardContent className="p-0">
            {/* Claims */}
            {activeTab === 'claims' && (claims.length > 0 ? (
              <div className="divide-y">{claims.map((c, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3" data-testid={`deleted-claim-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{c.report_no || 'No Report #'}</p>
                    <p className="text-xs text-gray-500">{c.company_name} &middot; {c.loss_type} &middot; Deleted {formatDate(c.deleted_at)}</p>
                  </div>
                  <ActionButtons type="claims" id={c.report_no} />
                </div>
              ))}</div>
            ) : <EmptyState />)}

            {/* Documents */}
            {activeTab === 'documents' && (documents.length > 0 ? (
              <div className="divide-y">{documents.map((d, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3" data-testid={`deleted-doc-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.file_name || 'Document'}</p>
                    <p className="text-xs text-gray-500">Claim: {d.report_no} &middot; Deleted {formatDate(d.deleted_at)}</p>
                  </div>
                  <ActionButtons type="documents" id={`${d.report_no}|${d.deleted_at}`} />
                </div>
              ))}</div>
            ) : <EmptyState />)}

            {/* Notes */}
            {activeTab === 'notes' && (notes.length > 0 ? (
              <div className="divide-y">{notes.map((n) => (
                <div key={n.note_id} className="flex items-center justify-between px-4 py-3" data-testid={`deleted-note-${n.note_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{n.text || 'Note'}</p>
                    <p className="text-xs text-gray-500">Claim: {n.report_no} &middot; By {n.author_name || 'Unknown'} &middot; Deleted {formatDate(n.deleted_at)}{n.deleted_by_name ? ` by ${n.deleted_by_name}` : ''}</p>
                  </div>
                  <ActionButtons type="notes" id={n.note_id} />
                </div>
              ))}</div>
            ) : <EmptyState />)}

            {/* Users */}
            {activeTab === 'users' && (users.length > 0 ? (
              <div className="divide-y">{users.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between px-4 py-3" data-testid={`deleted-user-${u.user_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600 shrink-0">
                      {u.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                      <p className="text-xs text-gray-500">{u.email} &middot; {u.role} &middot; Deleted {formatDate(u.deleted_at)}</p>
                    </div>
                  </div>
                  <ActionButtons type="users" id={u.user_id} />
                </div>
              ))}</div>
            ) : <EmptyState />)}

            {/* Companies */}
            {activeTab === 'companies' && (companies.length > 0 ? (
              <div className="divide-y">{companies.map((c) => (
                <div key={c.name} className="flex items-center justify-between px-4 py-3" data-testid={`deleted-company-${c.name}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                    <p className="text-xs text-gray-500">Deleted {formatDate(c.deleted_at)}{c.deleted_by_name ? ` by ${c.deleted_by_name}` : ''}</p>
                  </div>
                  <ActionButtons type="companies" id={c.name} />
                </div>
              ))}</div>
            ) : <EmptyState />)}

            {/* Organizations */}
            {activeTab === 'organizations' && (organizations.length > 0 ? (
              <div className="divide-y">{organizations.map((o) => (
                <div key={o.org_id} className="flex items-center justify-between px-4 py-3" data-testid={`deleted-org-${o.org_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{o.name}</p>
                    <p className="text-xs text-gray-500">Deleted {formatDate(o.deleted_at)}{o.deleted_by_name ? ` by ${o.deleted_by_name}` : ''}</p>
                  </div>
                  <ActionButtons type="organizations" id={o.org_id} />
                </div>
              ))}</div>
            ) : <EmptyState />)}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RecycleBin;
